import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// @ts-expect-error Will convert later
import usePrevious from '../../../hooks/usePrevious';

import Card from '../../Card/Card';

import {
  canMove,
  createParametersObject,
  FilterItem,
  getNextValue,
  ParametersObject,
  resetFollowingParams,
} from './helpers';
import './ParameterFilter.scss';
import {
  BulletinVariable,
  BulletinVariableFilter,
} from '../../BulletinView/types';

interface ParameterFilterProps {
  filterParameters?: ParametersObject;
  variable?: BulletinVariable;
  update?: (newFilter: Partial<ParametersObject>) => void;
  prevEntryLink: string | null;
  nextEntryLink: string | null;
  isLoading?: boolean;
}

const ParameterFilter = ({
  filterParameters,
  variable,
  update = () => {},
  prevEntryLink,
  nextEntryLink,
  isLoading,
}: ParameterFilterProps) => {
  const navigate = useNavigate();
  const prevFilterParameters = usePrevious(filterParameters);

  const getOptions = useCallback(
    (filter: ParametersObject, param: keyof BulletinVariableFilter) => {
      if (!variable) {
        return [];
      }

      const availableValues: Array<string | number> = variable.graphs.reduce(
        (list, item) => {
          if (!list.includes(item[param])) {
            list.push(item[param]);
          }
          return list;
        },
        [] as Array<string | number>,
      );

      let filteredVariables = availableValues;

      if (param in variable.filters) {
        // Make sure to keep the order of items sent by the backend
        filteredVariables = variable.filters[param].filter(v =>
          availableValues.includes(v),
        );
      }

      if (param === 'domain') {
        return filteredVariables;
      }
      let options = [...variable.graphs];
      if (filter.domain) {
        options = options.filter(graph => graph.domain === filter.domain);
      }
      if (param === 'depth' && filter.diagnostic_type) {
        options = options.filter(
          graph => graph.diagnostic_type === filter.diagnostic_type,
        );
      }

      return [...new Set(options.map(graph => graph[param]))].sort((a, b) => {
        if (typeof a === 'string' && typeof b === 'string') {
          if (a === 'map-D') {
            return -1;
          }
          if (b === 'map-D') {
            return 1;
          }
          return a.localeCompare(b);
        }
        if (typeof a === 'string') {
          return a.localeCompare(b.toString());
        }
        if (typeof b === 'string') {
          return a.toString().localeCompare(b);
        }
        return a - b;
      });
    },
    [variable],
  );

  const getParameters = useCallback(
    (filter = filterParameters) => {
      const MAP_SORT: Record<keyof BulletinVariableFilter, number> = {
        domain: 1,
        diagnostic_type: 2,
        depth: 3,
      };

      const filters: FilterItem[] = [];
      let options = [];
      let currentOption = '';
      if (variable) {
        (
          Object.keys(variable.filters || {}) as Array<
            keyof BulletinVariableFilter
          >
        )
          .sort((a, b) => MAP_SORT[a] - MAP_SORT[b])
          .forEach(param => {
            if (variable.filters[param].length > 1) {
              options = getOptions(filter, param);
              // TODO: document why we remove clim-RMSDobs
              options = options.filter(option => option !== 'clim-RMSDobs');
              currentOption =
                filter[param] !== undefined ? filter[param] : options[0];

              filters.push({
                options,
                currentOption,
                name: param,
              });
            }
          });
      }
      return filters;
    },
    [filterParameters, getOptions, variable],
  );

  const handleFilter = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = event.target;
      const nextFilterParameters = { ...filterParameters };
      const test = name as keyof ParametersObject;
      if (test === 'depth' && !Number.isNaN(parseInt(value, 10))) {
        nextFilterParameters[test] = Number(value);
      } else if (test !== 'depth') {
        nextFilterParameters[test] = value;
      }
      // reinit value of right filters
      if (name !== 'depth') {
        delete nextFilterParameters.depth;
      }
      if (name === 'domain') {
        delete nextFilterParameters.diagnostic_type;
      }
      update(createParametersObject(getParameters(nextFilterParameters)));
    },
    [filterParameters, getParameters, update],
  );

  const parameters = useMemo(() => getParameters(), [getParameters]);

  const currentParameters = useMemo(
    () => createParametersObject(parameters),
    [parameters],
  );

  useEffect(() => {
    // Make sure to reset the parameters when changing variables
    update({});
  }, [variable?.tree_key, update]);

  useEffect(() => {
    if (
      Object.keys(prevFilterParameters ?? {}).length === 0 &&
      Object.keys(currentParameters) &&
      JSON.stringify(prevFilterParameters) !== JSON.stringify(currentParameters)
    ) {
      update(currentParameters);
    }
  }, [currentParameters, prevFilterParameters, update]);

  const isDisabled = useCallback(
    direction => {
      if (!parameters) {
        return 'disabled';
      }
      let shouldDisabled = true;
      let index = 0;
      parameters.forEach(param => {
        index = direction === 'next' ? param.options.length - 1 : 0;
        if (param.options.indexOf(param.currentOption) !== index) {
          shouldDisabled = false;
        }
      });
      return shouldDisabled;
    },
    [parameters],
  );

  const moveOption = useCallback(
    direction => {
      for (let i = parameters.length - 1; i >= 0; i -= 1) {
        const param = parameters[i];

        if (canMove(param, direction)) {
          const nextFilterParameters: ParametersObject = {
            ...currentParameters,
            [param.name]: getNextValue(param, direction),
          };
          if (param.name !== 'depth') {
            delete nextFilterParameters.depth;
          }
          if (param.name === 'domain') {
            delete nextFilterParameters.diagnostic_type;
          }
          update(createParametersObject(getParameters(nextFilterParameters)));
          return;
        }

        if (i > 0) {
          const previousParam = parameters[i - 1];

          if (canMove(previousParam, direction)) {
            const nextFilterParameters: ParametersObject = {
              ...currentParameters,
              [previousParam.name]: getNextValue(previousParam, direction),
              ...resetFollowingParams(parameters, i, direction),
            };
            if (previousParam.name !== 'depth') {
              delete nextFilterParameters.depth;
            }
            if (previousParam.name === 'domain') {
              delete nextFilterParameters.diagnostic_type;
            }
            update(createParametersObject(getParameters(nextFilterParameters)));
            return;
          }
        }
      }
    },
    [currentParameters, getParameters, parameters, update],
  );

  const handleClick = useCallback(
    event => {
      const buttonType = event.currentTarget.getAttribute('data-action');
      const entryLink =
        buttonType === 'previous' ? prevEntryLink : nextEntryLink;

      // hasNextPrevEntry : the current button is a link to the next/prev entry in the bulletin menu
      // otherwise, it's a button to go throught the parameter of the current entity
      const hasNextPrevEntry = isDisabled(buttonType) && entryLink;

      if (hasNextPrevEntry) {
        navigate({ pathname: entryLink });
        update?.({});
      } else {
        moveOption(buttonType);
      }
    },
    [isDisabled, moveOption, navigate, nextEntryLink, prevEntryLink, update],
  );

  const isDisabledPreviousButton =
    (isDisabled('previous') && !prevEntryLink) || isLoading;
  const isDisabledNextButton =
    (isDisabled('next') && !nextEntryLink) || isLoading;

  const prevEntryLinkLabel = prevEntryLink?.includes('/v/')
    ? 'variable'
    : 'report';
  const nextEntryLinkLabel = nextEntryLink?.includes('/v/')
    ? 'variable'
    : 'report';

  return (
    <Card className="ParameterFilter">
      <div className="filters-items">
        <button
          type="button"
          className={`prevNextButton${
            isDisabledPreviousButton ? ' disabled' : ''
          }`}
          onClick={handleClick}
          data-action="previous"
          disabled={isDisabledPreviousButton}
        >
          <span className="icon icon-arrow-left left-arrow" />
          <span>
            Previous
            {isDisabled('previous') &&
              prevEntryLink &&
              ` ${prevEntryLinkLabel}`}
          </span>
        </button>
        {parameters?.map(filter => {
          if (filter.options.length === 0) {
            return null;
          }
          return (
            <div className="moniqua-select" key={`filter-${filter.name}`}>
              {filter.name}
              <select
                name={filter.name}
                value={filter.currentOption}
                onChange={handleFilter}
              >
                {filter.options.map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
        <button
          type="button"
          className={`prevNextButton${isDisabledNextButton ? ' disabled' : ''}`}
          onClick={handleClick}
          data-action="next"
          disabled={isDisabledNextButton}
        >
          <span>
            Next
            {isDisabled('next') && nextEntryLink && ` ${nextEntryLinkLabel}`}
          </span>
          <span className="icon icon-arrow-right right-arrow" />
        </button>
      </div>
    </Card>
  );
};

export default ParameterFilter;
